import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";

import { Box, IconButton, Typography, Button, Tooltip } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlined from "@mui/icons-material/FavoriteBorderOutlined";
import ModeCommentOutlined from "@mui/icons-material/ModeCommentOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import SellOutlined from "@mui/icons-material/SellOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import { styled } from "@mui/material/styles";
import FullscreenImagePopup from "./FullscreenImagePopup";
import AlertPopup from "./components/AlertPopup";
import UserCard from "./components/UserCard";
import FollowButton from "./components/FollowButton";
import FloatingMenu from "./components/FloatingMenu";
import ReportDialog from "./components/ReportDialog";
import CommentDialog from "./components/CommentDialog";
import SharePopup from "./components/SharePopup";
import { isValidUUIDv4 } from "./hooks/uuid";

// Import Avatar images

import Avatar3 from "./assets/SearchResults/Avatars/Robert_Thompson.png";

// Import images
import paypalImage from "./assets/paypal.png";
import visaImage from "./assets/visa.png";
import mastercardImage from "./assets/MC.png";
import discoverImage from "./assets/discover.png";
import dImage from "./assets/D.png";

import dayjs from "dayjs";

import { 
  TOY_DETAIL_BY_UUID,
  CREATE_TOY_LIKE_MUTATION,
  DELETE_TOY_LIKE_MUTATION,
  UPDATE_TOY_SHARE_NO_BY_UUID,
  CREATE_TOY_COMMENT_MUTATION,
  DELETE_TOY_COMMENT_MUTATION,
  EDIT_TOY_COMMENT_MUTATION,
  CREATE_TOY_COMMENT_LIKE_MUTATION,
  DELETE_TOY_COMMENT_LIKE_MUTATION,
  CREATE_TOY_COMMENT_REPLY_MUTATION,
  DELETE_TOY_COMMENT_REPLY_MUTATION,
  EDIT_TOY_COMMENT_REPLY_MUTATION,
  CREATE_TOY_COMMENT_REPLY_LIKE_MUTATION,
  DELETE_TOY_COMMENT_REPLY_LIKE_MUTATION,
} from "./graphQL/toy_qry";

import { CREATE_COLLECTION_MUTATION, DELETE_COLLECTION_MUTATION } from "./graphQL/collection_qry";

import { 
  CREATE_FOLLOW_MUTATION,
  DELETE_FOLLOW_MUTATION 
} from "./graphQL/follow_qry";


//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const ImageGalleryContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: "20px",
  paddingLeft: 0,
  "@media (max-width: 1360px)": {
    flexDirection: "column",
  },
  "@media (max-width: 856px)": {
    flexDirection: "column",
    paddingRight: 0,
  },
}));

const ThumbnailContainer = styled(Box)(() => ({
  width: "90px",
  height: "500px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  overflow: "hidden",
  padding: 0,
  margin: 0,
  boxSizing: "border-box",
  "@media (max-width: 1360px)": {
    width: "100%",
    height: "auto",
    flexDirection: "row",
    overflow: "hidden",
    paddingTop: "10px",
  },
}));

const ThumbnailImage = styled("img", {
  shouldForwardProp: prop => prop !== "active",
})(({ active }) => ({
  width: "80px",
  height: "80px",
  objectFit: "contain",
  cursor: "pointer",
  border: active ? "2px solid #EF5658" : "2px solid transparent",
  borderRadius: "10px",
  transition: "border-color 0.2s ease",
  display: "block",
  margin: 0,
  padding: 0,
  boxSizing: "border-box",
  backgroundColor: "#f9f9f9",
  "@media (max-width: 1360px)": {
    width: "61.5px",
    height: "61.5px",
  },
  "@media (max-width: 856px)": {
    width: "56.86px",
    height: "56.86",
  },
}));

const MainImageContainer = styled(Box)(() => ({
  position: "relative",
  width: "520px",
  height: "498px",
  backgroundColor: "#f5f5f5",
  borderRadius: "10px",
  overflow: "hidden",
  "@media (max-width: 1360px)": {
    width: "389px",
    height: "389px",
    pl: 0,
  },
  "@media (max-width: 856px)": {
    width: "361px",
    height: "361px",
    pl: 0,
  },
}));

const MainImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const NavigationButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  color: "#333",
  width: "34px",
  height: "34px",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
  },
  zIndex: 2,
}));

const FullscreenButton = styled(IconButton)(() => ({
  position: "absolute",
  bottom: "16px",
  right: "16px",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  color: "#666",
  width: "32px",
  height: "32px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
  },
  zIndex: 2,
  "@media (max-width: 1360px)": {
    right: "10px",
  },
}));

const ProductDetail = () => {
  const navigate = useNavigate();
  const { toyUuid } = useParams();

  const [productData, setProductData] = useState({});
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const [isInfoPopupOpen, setIsInfoPopupOpen] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "", // "add" or "remove"
    title: "",
  });
  const [warningPopup, setWarningPopup] = useState({
    open: false,    
  });
  const [isInvalidURL, setIsInvalidURL] = useState(false);
  let role = localStorage.getItem("role") ? localStorage.getItem("role") : null;
  
  //remove from collection by sami
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    title: "",
});
  
  //add to collection by sami
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    title: "",
});	

//follow by sami
const [createFollow] = useMutation(CREATE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unFollow] = useMutation(DELETE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [reportDialog, setReportDialog] = useState({ open: false });

  const [unfollowPopup, setUnfollowPopup] = useState({
    open: false,
    followingName: "",
  });

  const [commentDialog, setCommentDialog] = useState({
    open: false,
    product: null,
  });
  const [shareDialog, setShareDialog] = useState({ open: false });
  const [isFollowing, setIsFollowing] = useState(false);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);

  const [createLike] = useMutation(CREATE_TOY_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unLike] = useMutation(DELETE_TOY_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [updateToyShare] = useMutation(UPDATE_TOY_SHARE_NO_BY_UUID, {
    awaitRefetchQueries: true,
  });

  const [createComment] = useMutation(CREATE_TOY_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [editComment] = useMutation(EDIT_TOY_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteComment] = useMutation(DELETE_TOY_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createToyCommentLike] = useMutation(CREATE_TOY_COMMENT_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unLikeToyComment] = useMutation(DELETE_TOY_COMMENT_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createToyCommentReply] = useMutation(CREATE_TOY_COMMENT_REPLY_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [editToyCommentReply] = useMutation(EDIT_TOY_COMMENT_REPLY_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteToyCommentReply] = useMutation(DELETE_TOY_COMMENT_REPLY_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createToyCommentReplyLike] = useMutation(CREATE_TOY_COMMENT_REPLY_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unLikeToyCommentReply] = useMutation(DELETE_TOY_COMMENT_REPLY_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;
  const loggedUserName = localStorage.getItem("userName") || "";

  const infoPopupRef = useRef(null);  

  // David
  // Get data from database
  const [attributes, setAttributes] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [tags, setTags] = useState([]);

  const [getToyAttributes] = useManualQuery(TOY_DETAIL_BY_UUID);

  //Sami
  const [createCollection] = useMutation(CREATE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteCollection] = useMutation(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const encodeText = (text) => {
    return encodeURIComponent(text);
  };

  const setImages = db => {
    const images = db.toyImages?.map(img => img.fileName) || [];
    setProductImages(images);
    setCurrentImageIndex(images.length > 1 ? images.length - 1 : 0);
  };

  const setTagsVal = db => {
    if (db && db.length > 0) {
      const formatted = db.map((t, index) => ({
        id: t.tagId.tagId,
        value: t.tagId.title,
      }));
      setTags(formatted);
    }
  };

  const mapToyAttributes = db => {
    return [
      {
        id: 0,
        label: "Brand",
        value: db.brandId?.title ?? "",
      },
      {
        id: 1,
        label: "Category",
        value: db.categoryId?.title ?? "",
      },
      {
        id: 2,
        label: "Subcategory",
        value: db.subCategoryId?.title ?? "",
      },
      {
        id: 3,
        label: "Condition",
        value: db.conditionId?.title ?? "",
      },
      {
        id: 4,
        label: "Warning",
        value: db.warningId?.title ?? "",
      },
    ];
  };

  const mapToySpecifications = db => {
    return [
      {
        id: 0,
        label: "Age Group",
        value: db.ageGroupId?.title ?? "",
      },
      {
        id: 1,
        label: "Material",
        value: db.materialId?.title ?? "",
      },
      {
        id: 2,
        label: "Dimensions",
        value: db.dimension ?? "",
      },
      {
        id: 3,
        label: "Weight",
        value: db.weight ?? "",
      },
      {
        id: 4,
        label: "Color",
        value: db.colorId?.title ?? "",
      },
      {
        id: 5,
        label: "Sub-Color",
        value: db.subColorId?.title ?? "",
      },
      {
        id: 6,
        label: "No of Players",
        value: db.noOfPlayers ?? "",
      },
      {
        id: 7,
        label: "Battery Required",
        value: db.batteryRequired ? "Yes" : "No",
      },
      {
        id: 8,
        label: "Battery Included",
        value: db.batteryIncluded ? "Yes" : "No",
      },
      {
        id: 9,
        label: "Assembly Required",
        value: db.assemblyRequired ? "Yes" : "No",
      },
      {
        id: 10,
        label: "Level of Difficulty",
        value: db.difficultyId?.title ?? "",
      },
      {
        id: 11,
        label: "Warning",
        value: db.warningId?.title ?? "",
      },
      {
        id: 12,
        label: "Game Duration",
        value: db.gameDuration ?? "",
      },
      {
        id: 13,
        label: "Manufacturer Country",
        value: db.manufacturerCountryId?.name ?? "",
      },
    ];
  };

  useEffect(() => {
      setToyAttributeslist();
  }, []);

  const setToyAttributeslist = async () => {
    if (!toyUuid) {
      setIsInvalidURL(true);
      return;
    }
    if (isValidUUIDv4(toyUuid)){
        const {
          loading: loadingToyAttributes,
          error: errorToyAttributes,
          data: dataToyAttributes,
        } = await getToyAttributes({
          variables: {
            toyUuid,
          },
        });
        if (!loadingToyAttributes && dataToyAttributes && dataToyAttributes.toyDetailByUuid) {
          if (role === "Admin" || 
            dataToyAttributes.toyDetailByUuid.isPublished === true ||
            dataToyAttributes.toyDetailByUuid.userId.userUuid === userUuid)
          {
            setProductData(prev => ({
              ...prev,
              ...dataToyAttributes.toyDetailByUuid,
              comments: dataToyAttributes.toyDetailByUuid.toyComments,
            }));
            setLikeCount(dataToyAttributes.toyDetailByUuid.likeNo);
            setIsLiked(dataToyAttributes.toyDetailByUuid.isLiked);
            setShareCount(dataToyAttributes.toyDetailByUuid.shareNo);
            setCommentCount(dataToyAttributes.toyDetailByUuid.commentNo);
            setAttributes(mapToyAttributes(dataToyAttributes.toyDetailByUuid));
            setSpecifications(mapToySpecifications(dataToyAttributes.toyDetailByUuid));
            setImages(dataToyAttributes.toyDetailByUuid);
            setTagsVal(dataToyAttributes.toyDetailByUuid.toyTags);
            setIsBookmarked(dataToyAttributes.toyDetailByUuid.isInCollection);
            setIsFollowing(dataToyAttributes.toyDetailByUuid.userId.isLoggedInUserFollowing);
          }
          else {
            setWarningPopup({
              open: true,
              title: "This page is not available at this time.",
            });        
          }
        }
        else
          setIsInvalidURL(true);
        if (errorToyAttributes) {
          console.log("errorToyAttributes: ", errorToyAttributes);
          setIsInvalidURL(true);          
        }
    }
    else
      setIsInvalidURL(true);
  };

  // David End

  const handlePrevious = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentImageIndex < productImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  // Check if buttons should be disabled
  const isPrevDisabled = currentImageIndex === 0; // First image (index 0)
  const isNextDisabled = currentImageIndex === productImages.length - 1; // Last image (index 5)

  const handleThumbnailClick = index => {
    setCurrentImageIndex(index);
  };

  const handleLikeToggle = async() => {
    //save toy like/unlike in database
    if (!isLiked){
      const { data, error } =  await createLike({
        variables: {
          toyUuid,                
        },
      });            
         
      if (error) {
        console.log("Error in create toy like", error, data);             
      } else {
        console.log("Success in create toy like",data);
        setIsLiked(!isLiked);
        setLikeCount(prevCount => (isLiked ? prevCount - 1 : prevCount + 1));
      };
    } else {
      const { data, error } =  await unLike({
        variables: {
          toyUuid,                
        },
      });

      if (error) {
        console.log("Error in delete toy like", error, data);      
      } else {
        console.log("Success in delete toy like",data);
        setIsLiked(!isLiked);
        setLikeCount(prevCount => (isLiked ? prevCount - 1 : prevCount + 1));
      };
    };
  };

  const handleFullscreenOpen = () => {
    setIsFullscreenOpen(true);
  };

  const handleFullscreenClose = () => {
    setIsFullscreenOpen(false);
  };

  const handleFullscreenPrevious = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleFullscreenNext = () => {
    if (currentImageIndex < productImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };

  const handleFollowToggle = async() => {

    if (isFollowing) {
      // Show unfollow confirmation popup
      setUnfollowPopup({
        open: true,
        followingName: productData.userId.firstName + " " + productData.userId.lastName,
      });
    }
    else
    {
      //save follow to database
      const { data, error } =  await createFollow({
        variables: {
          userUuid: productData.userId.userUuid,        
        },
      });
            
      if (error) {
        console.log("Error", error, data);             
      } 
      else {
        console.log("Success",data);
        // Direct follow action
        setIsFollowing(!isFollowing);                 
      }    
    };
  }

  const handleConfirmUnfollow = async() => {
    //save unfollow to database
    const { data, error } =  await unFollow({
      variables: {
        userUuid:  productData.userId.userUuid,         
      },
    });

    if (error) {
      console.log("Error", error, data);      
    } else {
      console.log("Success",data);
      setIsFollowing(!isFollowing);
      setUnfollowPopup({ open: false, followingName: "" });
    }
  };

  const handleCancelUnfollow = () => {
    setUnfollowPopup({ open: false, followingName: "" });
  };

    

  const handleCommentLikeUpdate = async(commentUuid, isLiked) => {
    //save toy comment like/unlike in database
    if (isLiked){
      const { data, error } =  await createToyCommentLike({
        variables: {
          toyCommentUuid: commentUuid,
        },
      });         
      if (error) {
        console.log("Error in create toy comment like", error, data);             
      } else {
        console.log("Success in create toy comment like",data);
      };
    } else {
      const { data, error } =  await unLikeToyComment({
        variables: {
          toyCommentUuid: commentUuid,
        },
      });
      if (error) {
        console.log("Error in delete toy comment like", error, data);      
      } else {
        console.log("Success in delete toy comment like",data);          
      };
    };
    setProductData(prev => ({
      ...prev,
      comments: prev.comments.map(comment =>
        comment.uuid === commentUuid
        ? {
            ...comment,
            isLiked,
            likeNo: isLiked 
            ? comment.likeNo + 1
            : comment.likeNo > 0
            ? comment.likeNo - 1
            : 0,
          }
        : comment
      ),
    }));  
  };

  const handleCommentReplyLikeUpdate = async(
    commentUuid,
    commentReplyUuid,
    isLiked,
  ) => {
    //save toy comment reply like/unlike in database
    if (isLiked){
      const { data, error } =  await createToyCommentReplyLike({
        variables: {
          toyCommentReplyUuid: commentReplyUuid,
        },
      });            
         
      if (error) {
        console.log("Error in create toy comment reply like", error, data);
        return
      } else {
        console.log("Success in create toy comment reply like",data);
      };
    } else {
      const { data, error } =  await unLikeToyCommentReply({
        variables: {
          toyCommentReplyUuid: commentReplyUuid,
        },
      });

      if (error) {
        console.log("Error in delete toy comment reply like", error, data);
        return
      } else {
        console.log("Success in delete toy comment reply like",data);
      };
    };
    setProductData(prev => ({
      ...prev,
      comments: prev.comments &&
      prev.comments.map(comment =>
        comment.uuid === commentUuid
        ? {
            ...comment,
            replies: comment.replies &&
            comment.replies.map(commentReply =>
              commentReply.uuid === commentReplyUuid
              ? {
                  ...commentReply,
                  isLiked,
                  likeNo: isLiked ?
                    commentReply.likeNo + 1
                  : commentReply.likeNo > 0
                    ? commentReply.likeNo - 1
                    : 0,
                }
              : commentReply
            ),
          }
        : comment
      ),
    }));
  };

  const handleCommentClick = () => {
    setCommentDialog({
      open: true,
      product: productData,
    });
  };

  // Handle cancel removal
  const handleCloseCommentDlg = () => {
    setCommentDialog({ open: false, product: null });
  };

  const handleAddComment = async(comntText) => {
    if (!comntText.trim()) return;
    let commentText = encodeText(comntText);
    //Save toy comment in database
    const { data, error } =  await createComment({
      variables: {
        toyUuid: toyUuid,
        content: commentText,                
      },
    });            
       
    if (error) {
      console.log("Error in creating toy comment", error, data);             
    } else {
      console.log("Success in creating toy comment",data);
      setProductData(prev => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          data.createToyComment.toyComment,
        ],
      }));
      setCommentCount(commentCount + 1);
    };   
  };

  const handleAddReply = async(repText, commentUuid) => {
    if (!repText.trim() || !commentUuid) return;
    let replyText = encodeText(repText);

    //Save toy comment reply in database
    const { data, error } =  await createToyCommentReply({
      variables: {
        toyCommentUuid: commentUuid,
        content: replyText,                
      },
    });            
       
    if (error) {
      console.log("Error in creating toy comment reply", error, data);
    } else {
      console.log("Success in creating toy comment reply",data);
      setProductData(prev => ({
        ...prev,
        comments: prev.comments.map(comment =>
          comment.uuid === commentUuid
          ? {
              ...comment,
              replies: 
                [...(comment.replies || []), 
                data.createToyCommentReply.toyCommentReply],
            }
          : comment
        ),
      }));
    };
  };

  const handleDeleteComment = async(commentUuid) => {
    if (!commentUuid) return;
    const { data, error } =  await deleteComment({
      variables: {
        toyCommentUuid: commentUuid,                
      },
    });

    if (error) {
      console.log("Error in delete toy comment", error, data);
    } else {
      console.log("Success in delete toy comment",data);
      setProductData(prev => ({
        ...prev,
        comments: prev.comments.filter(c => c.uuid !== commentUuid),
      }));
      setCommentCount(commentCount - 1);
    };
  };

  const handleDeleteReply = async(commentUuid, replyUuid) => {
    const { data, error } =  await deleteToyCommentReply({
      variables: {
        toyCommentReplyUuid: replyUuid,                
      },
    });

    if (error) {
      console.log("Error in delete toy comment reply", error, data);
    } else {
      console.log("Success in delete toy comment reply",data);
      setProductData(prev => ({
        ...prev,
        comments: prev.comments.map(comment =>
          comment.uuid === commentUuid
          ? {
              ...comment,
              replies: comment.replies.filter(
                reply => reply.uuid !== replyUuid
              ),
            }
          : comment
        ),
      }));
    };
  };

  const handleEditComment = async(commentUuid, newEditText) => {
    if (!commentUuid || !newEditText.trim()) return;
    let newText = encodeText(newEditText);
    //Edit toy comment in database
    const { data, error } =  await editComment({
      variables: {
        toyCommentUuid: commentUuid,
        content: newText,                
      },
    });            
       
    if (error) {
      console.log("Error in editing toy comment", error, data);
    } else {
      console.log("Success in editing toy comment",data);
      setProductData(prev => ({
        ...prev,
        comments: prev.comments.map(c =>
          c.uuid === commentUuid ? { ...c, content: newText } : c
        ),
      }));
    };   
  };

  const handleEditReply = async(
    commentUuid,
    replyUuid, 
    newEditText
  ) => {
    if (!commentUuid || !replyUuid || !newEditText.trim()) return;
    let newText = encodeText(newEditText);
    //Edit toy comment reply in database
    const { data, error } =  await editToyCommentReply({
      variables: {
        toyCommentReplyUuid: replyUuid,
        content: newText,                
      },
    });            
       
    if (error) {
      console.log("Error in editing toy reply comment", error, data);
    } else {
      console.log("Success in editing toy reply comment",data);
      setProductData(prev => ({
        ...prev,
        comments: prev.comments.map(comment =>
          comment.uuid === commentUuid
          ? {
            ...comment,
            replies: comment.replies.map(reply =>
              reply.uuid === replyUuid
              ? { ...reply, content: newText }
              : reply
            ),
          }
        : comment
        ),
      }));
    };
  };

  const handleInfoPopupOpen = () => {
    setIsInfoPopupOpen(true);
  };

  const handleBookmarkClick = () => {
    if (isBookmarked) {
    // Show removal confirmation 
    setConfirmPopup({
      open: true,
      title: productData.title,
    });
  } else {
    // Show add alert
    setSuccessPopup({
      open: true,
      title: productData.title,
    }); 
   }  
  };

  const handleConfirmAdd = async () => {
  await createCollection({
    variables: {
      objectId: parseInt(productData.toyId),
      contentType: "toy",
    },
  });

  setIsBookmarked(true);
  setSuccessPopup({ open: false, title: "" });
};

  const handleConfirmRemove = async () => {
  await deleteCollection({
    variables: {
      objectId: parseInt(productData.toyId),
      contentType: "toy",
    },
  });

  setIsBookmarked(false);
  setConfirmPopup({ open: false, title: "" });
};

  // Handle cancel action
  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", title: "" });
  };

  // Handle Report Dialog Open Action
  const handleReportDialogOpen = () => {
    setReportDialog({
      open: true,
    });
  };

  // Handle Report Dialog Close action
  const handleReportDialogClose = () => {
    setReportDialog({ open: false });
  };

  // Handle Share Dialog Open Action
  const handleShareDialogOpen = () => {
    setShareDialog({ open: true });
  };

  const handleToyShareSuccess = async() => {
    if (!toyUuid) return
    const { data, error } =  await updateToyShare({
      variables: {
        toyUuid,                
      },
    });            
         
    if (error) {
      console.log("Error in updating toy share", error, data);             
    } else {
      console.log("Success in updating toy share",data);
      setShareCount(prevCount => prevCount + 1);
    };
  };

  // Handle Share Dialog Close action
  const handleShareDialogClose = () => {
    setShareDialog({ open: false });
  };

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  // Handle click outside to close popups
  useEffect(() => {
    const handleClickOutside = event => {
      if (infoPopupRef.current && !infoPopupRef.current.contains(event.target)) {
        setIsInfoPopupOpen(false);
      }
    };

    if (isInfoPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isInfoPopupOpen]);

  const dayFormatted = dateString => dayjs(dateString).format("MMM D, YYYY");

  return (
  <>
  {Object.keys(productData).length > 0 ? (
    <Box
      sx={{
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <Helmet>
        <title>R3PLAY - Toy Details</title>
        <meta
          name="description"
          content="View detailed information about this toy. Learn its story, owner, and purchase or trade options"
        />
        <meta
          property="keywords"
          content="Toy details, toy story, collectible toys, buy used toys, toy listings"
        />
      </Helmet>
      {/* Product Page Content*/}
      <Box
        sx={{
          backgroundColor: "#f8f8f8",
          pt: 1,
          pb: 4,
        }}
      >
        {/* Two Column Layout */}
        <Box
          sx={{
            px: "100px",
            display: "flex",
            "@media (max-width: 1360px)": {
              px: "16px",
            },
            "@media (max-width: 856px)": {
              flexDirection: "column",
              alignItems: "center",
            },
          }}
        >
          {/* LEFT COLUMN - Image Gallery & Specifications */}
          <Box>
            {/* Image Gallery Section */}
            <ImageGalleryContainer>
              {/* Thumbnail Navigation */}
              <Box
                sx={{
                  "@media (max-width: 1360px)": {
                    order: 2,
                  },
                }}
              >
                <ThumbnailContainer>
                  {productImages.map((image, index) => (
                    <ThumbnailImage
                      key={index}
                      src={image}
                      alt={`Product view ${index + 1}`}
                      active={currentImageIndex === index}
                      onClick={() => handleThumbnailClick(index)}
                    />
                  ))}
                </ThumbnailContainer>
              </Box>

              {/* Main Image Display */}
              <MainImageContainer>
                <MainImage
                  src={productImages[currentImageIndex]}
                  alt={`Product main view ${currentImageIndex + 1}`}
                />

                {/* Navigation Arrows */}
                {!isPrevDisabled && (
                  <NavigationButton
                    onClick={handlePrevious}
                    sx={{
                      left: "16px",
                      "@media (max-width: 1360px)": {
                        left: "8px",
                      },
                    }}
                    aria-label="Previous image"
                  >
                    <NavigateBeforeIcon />
                  </NavigationButton>
                )}

                {!isNextDisabled && (
                  <NavigationButton
                    onClick={handleNext}
                    sx={{
                      right: "16px",
                      "@media (max-width: 1360px)": {
                        right: "8px",
                      },
                    }}
                    aria-label="Next image"
                  >
                    <NavigateNextIcon />
                  </NavigationButton>
                )}

                {/* Fullscreen Button */}
                <FullscreenButton
                  onClick={handleFullscreenOpen}
                  aria-label="Fullscreen view"
                >
                  <FullscreenOutlinedIcon />
                </FullscreenButton>
              </MainImageContainer>
            </ImageGalleryContainer>

            {/* Gallery Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "608px",
                "@media (max-width: 1360px)": {
                  width: "390px",
                },
                "@media (max-width: 856px)": {
                  width: "361px",
                },
                mb: 1,
              }}
            >
              {/* Left side - Like, Comment, Share buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <StyledTooltip title={isLiked ? "Click to undo like" : "Click to like"}>
                  <Button
                    onClick={handleLikeToggle}
                    startIcon={
                      isLiked ? (
                        <FavoriteIcon sx={{ fontSize: "18px", color: "#EF5658" }} />
                      ) : (
                        <FavoriteBorderOutlined
                          sx={{ fontSize: "18px", color: "#999" }}
                        />
                      )
                    }
                    sx={{
                      border: "1px solid #ddd",
                      color: "#999",
                      fontSize: "14px",
                      textTransform: "none",
                      height: "32px",
                      px: 2,
                      borderRadius: "4px",
                      "@media (max-width: 1360px)": {
                        px: 1,
                        minWidth: "auto",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                      },
                      "&:hover": {
                        borderColor: "#EF5658",
                        color: "#EF5658",
                        "& .MuiSvgIcon-root": {
                          color: "#EF5658",
                        },
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        "@media (max-width: 1360px)": {
                          display: "none",
                        },
                      }}
                    >
                      Like
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {likeCount}
                    </Box>
                  </Button>
                </StyledTooltip>
                <StyledTooltip title="View/Add comments">
                  <Box onClick={handleCommentClick}>
                    <Button
                      startIcon={
                        <ModeCommentOutlined sx={{ fontSize: "18px", color: "#999" }} />
                      }
                      sx={{
                        border: "1px solid #ddd",
                        color: "#999",
                        fontSize: "14px",
                        textTransform: "none",
                        height: "32px",
                        px: 2,
                        borderRadius: "4px",
                        "@media (max-width: 1360px)": {
                          px: 1,
                          minWidth: "auto",
                          "& .MuiButton-startIcon": {
                            margin: 0,
                          },
                        },
                        "&:hover": {
                          borderColor: "#EF5658",
                          color: "#EF5658",
                          "& .MuiSvgIcon-root": {
                            color: "#EF5658",
                          },
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          "@media (max-width: 1360px)": {
                            display: "none",
                          },
                        }}
                      >
                        Comment
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        {commentCount}
                      </Box>
                    </Button>
                  </Box>
                </StyledTooltip>
                <StyledTooltip title="Share this item">
                  <Button
                    onClick={handleShareDialogOpen}
                    startIcon={<ShareOutlined sx={{ fontSize: "18px", color: "#999" }} />}
                    sx={{
                      border: "1px solid #ddd",
                      color: "#999",
                      fontSize: "14px",
                      textTransform: "none",
                      height: "32px",
                      px: 2,
                      borderRadius: "4px",
                      "@media (max-width: 1360px)": {
                        px: 1,
                        minWidth: "auto",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                      },
                      "&:hover": {
                        borderColor: "#EF5658",
                        color: "#EF5658",
                        "& .MuiSvgIcon-root": {
                          color: "#EF5658",
                        },
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        "@media (max-width: 1360px)": {
                          display: "none",
                        },
                      }}
                    >
                      Share
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {shareCount}
                    </Box>
                  </Button>
                </StyledTooltip>
                <StyledTooltip
                  title={
                    isBookmarked
                      ? "Remove from your collection"
                      : "Add to your collection"
                  }
                >
                  <Button
                    onClick={handleBookmarkClick}
                    startIcon={
                      isBookmarked ? (
                        <CollectionsBookmarkIcon fontSize="18px" sx={{ color: "#000" }} />
                      ) : (
                        <CollectionsBookmarkOutlinedIcon
                          fontSize="18px"
                          sx={{ color: "#999" }}
                        />
                      )
                    }
                    sx={{
                      border: "1px solid #ddd",
                      color: "#999",
                      fontSize: "14px",
                      textTransform: "none",
                      height: "32px",
                      px: 2,
                      borderRadius: "4px",
                      "@media (max-width: 1360px)": {
                        px: 1,
                        minWidth: "auto",
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                      },
                      "&:hover": {
                        borderColor: "#EF5658",
                        color: "#EF5658",
                        "& .MuiSvgIcon-root": {
                          color: "#EF5658",
                        },
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        "@media (max-width: 1360px)": {
                          display: "none",
                        },
                      }}
                    >
                      Collection
                    </Box>
                  </Button>
                </StyledTooltip>
              </Box>

              {/* Right side - Report */}
              <StyledTooltip title="Report this item">
                <Box
                  onClick={handleReportDialogOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    height: "32px",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#EF5658",
                      "& .MuiSvgIcon-root": {
                        color: "#EF5658",
                      },
                    },
                  }}
                >
                  <FlagOutlined
                    sx={{
                      fontSize: "18px",
                      color: "#999",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#999",
                      "&:hover": {
                        color: "#EF5658",
                      },
                    }}
                  >
                    Report
                  </Typography>
                </Box>
              </StyledTooltip>
            </Box>
            {/* Specifications Section - Desktop and Tablet*/}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                "@media (max-width: 856px)": {
                  display: "none",
                },
              }}
            >
              <Box sx={{ gap: 3, mt: 1 }}>
                <Box
                  sx={{
                    width: "608px",
                    height: "690px",
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.25)",
                    "@media (max-width: 1360px)": {
                      width: "390px",
                    },
                  }}
                >
                  {/* Specifications Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      p: 1,
                      pl: 2,
                      position: "relative",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#333",
                      }}
                    >
                      Specifications
                    </Typography>
                    <InfoOutlined
                      onClick={handleInfoPopupOpen}
                      sx={{
                        fontSize: "20px",
                        color: "#666",
                        cursor: "pointer",
                      }}
                    />

                    {/* Info Popup */}
                    {isInfoPopupOpen && (
                      <Box
                        ref={infoPopupRef}
                        sx={{
                          position: "absolute",
                          top: "calc(100%)",
                          left: 130,
                          width: "339px",
                          height: "130px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          zIndex: 1000,
                          p: 2,
                          "@media (max-width: 856px)": {
                            left: 10,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            mb: 1,
                          }}
                        >
                          Optional Details
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: "bold",
                            color: "#333",
                            mb: 1,
                          }}
                        >
                          Provided by the Seller
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            lineHeight: 1.4,
                          }}
                        >
                          These details are shared by the seller and may help you better
                          understand the product.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      backgroundColor: "#E0E0E0",
                      mb: 3,
                    }}
                  />

                  {/* Specifications Content */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      p: 2,
                      pt: 0,
                    }}
                  >
                    {specifications.map(spec => (
                      <Box key={spec.id} sx={{ display: "flex" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            width: "304px",
                            "@media (max-width: 1360px)": { width: "204px" },
                          }}
                        >
                          {spec.label}
                        </Typography>
                        <Typography sx={{ fontSize: "14px", color: "#666" }}>
                          {spec.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          {/* RIGHT COLUMN - Product Information */}
          {productData && (
          <Box
            sx={{
              flex: 1,
              mt: 2,
              maxWidth: "700px",
            }}
          >
            {/* User Profile Section */}
            <Box
              sx={{
                display: "flex",
                width: "350px",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >              
                <UserCard
                  user={productData.userId && productData.userId}
                  onUsernameClick={handleUserProfileNavigation}
                  onFullNameClick={handleUserProfileNavigation}
                  type="avatar"
                >
                  <Box
                    component="img"
                    src={
                      productData.userId && productData.userId.avatarFileName
                    }
                    alt="User Avatar"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                </UserCard>

                <Box>
                  <UserCard
                    user={productData.userId && productData.userId}
                    onUsernameClick={handleUserProfileNavigation}
                    onFullNameClick={handleUserProfileNavigation}
                    type="username"
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "medium",
                        color: "#EF5658",
                      }}
                    >
                      {productData.userId && productData.userId.username}
                    </Typography>
                  </UserCard>
                </Box>
              </Box>

                {/* Follow Button */}
                {productData.userId.userUuid !== userUuid && (
                
                <FollowButton isFollowing={isFollowing} onToggle={handleFollowToggle} />
                )}
            </Box>

            {/* Product Title */}
            <Typography
              sx={{
                fontSize: "12px",
                color: "#999",
                mb: 1,
              }}
            >
              Last Published: {dayFormatted(productData.publishedDate)}
            </Typography>

            {/* Product Title */}
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: "bold",
                color: "#333",
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              {productData.title}
            </Typography>

            {/* Product Description */}
            <Typography
              sx={{
                fontSize: "14px",
                color: "#666",
                lineHeight: 1.5,
                mb: 1.5,
              }}
            >
              {productData.description}
            </Typography>

            {/* Price Section */}
            <Box sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  mb: 1,
                }}
              >
                Price
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                <Typography
                  sx={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#EF5658",
                    lineHeight: 1,
                  }}
                >
                  ${productData.sellingPrice}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#999",
                    textDecoration: "line-through",
                  }}
                >
                  {Number(productData.originalPrice) > 0 &&
                    `($${productData.originalPrice})`}
                </Typography>
              </Box>
            </Box>
            {/* Dynamic Attributes */}
            <Box
              sx={{
                display: "flex",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
                "@media (max-width: 1360px)": { gap: 2 },
              }}
            >
              {attributes.map(attribute => (
                <Box
                  key={attribute.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#666",
                      mb: 1,
                      textAlign: "center",
                    }}
                  >
                    {attribute.label}
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #E0E0E0",
                      borderRadius: "4px",
                      px: 1,
                      py: 1,
                      display: "inline-block",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#999",
                        textAlign: "center",
                      }}
                    >
                      {attribute.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {/* Rehome This Toy Button */}
            <Button
              variant="contained"
              startIcon={<ShoppingCartOutlined />}
              sx={{
                backgroundColor: "#EF5658",
                color: "white",
                width: "214px",
                height: "42px",
                fontSize: "15px",
                fontWeight: "medium",
                textTransform: "uppercase",
                borderRadius: "4px",
                justifyContent: "center",
                paddingLeft: "22px",
                mb: 3,
                "&:hover": {
                  backgroundColor: "#D94446",
                },
                "& .MuiButton-startIcon": {
                  marginRight: "12px",
                },
              }}
            >
              Rehome This Toy
            </Button>

            {/* Product Details Section */}
            <Box
              sx={{
                pt: 3,
                pl: 0,
              }}
            >
              {/* Tags */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                    ml: 0.5,
                  }}
                >
                  <SellOutlined sx={{ fontSize: "16px", color: "#666" }} />
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>Tags</Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {tags.map(tag => (
                    <Box
                      key={tag.id}
                      sx={{
                        backgroundColor: "#e0e0e0",
                        borderRadius: "16px",
                        px: 2,
                        py: 0.75,
                        display: "inline-block",
                        cursor: "pointer",
                        border: "1px solid transparent",
                        "&:hover": {
                          backgroundColor: "rgba(239, 86, 88, 0.1)",
                          borderColor: "#EF5658",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#333",
                        }}
                      >
                        {tag.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Delivery */}
              <Box sx={{ mb: 2, display: "flex", flexDirection: "row" }}>
                <Box sx={{ width: "100px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#666", mb: 0 }}>
                    Delivery:
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "14px", color: "#666", flex: 2 }}>
                  Estimated between Wed, Jul 30 to Thurs, Jul 30
                </Typography>
              </Box>
              {/* Divider */}
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "#E0E0E0",
                  mt: 2,
                }}
              />

              {/* Returns */}
              <Box sx={{ mt: 1, mb: 2, display: "flex", flexDirection: "row" }}>
                <Box sx={{ width: "100px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
                    Returns:
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#666",
                    lineHeight: 1.4,
                    flex: 2,
                  }}
                >
                  30 days returns. Buyer pays for return shipping. If you use an eBay
                  shipping label, it will be deducted from your refund amount.
                </Typography>
              </Box>
              {/* Divider */}
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "#E0E0E0",
                  mt: 2,
                }}
              />

              {/* Payments */}
              <Box sx={{ mt: 1, mb: 2, display: "flex", flexDirection: "row" }}>
                <Box sx={{ width: "100px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
                    Payments:
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flex: 2 }}>
                  <Box
                    component="img"
                    src={paypalImage}
                    alt="PayPal"
                    sx={{
                      height: "32px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                  <Box
                    component="img"
                    src={visaImage}
                    alt="Visa"
                    sx={{
                      height: "32px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                  <Box
                    component="img"
                    src={mastercardImage}
                    alt="MasterCard"
                    sx={{
                      height: "32px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                  <Box
                    component="img"
                    src={discoverImage}
                    alt="Discover"
                    sx={{
                      height: "32px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                  <Box
                    component="img"
                    src={dImage}
                    alt="D"
                    sx={{
                      height: "32px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          )}
          {/* Specifications Section - Mobile only*/}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              pt: 3,
              "@media (min-width: 856.1px)": {
                display: "none",
              },
            }}
          >
            <Box sx={{ gap: 3, mt: 1 }}>
              <Box
                sx={{
                  width: "608px",
                  height: "690px",
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.25)",
                  "@media (max-width: 856px)": {
                    width: "361px",
                  },
                }}
              >
                {/* Specifications Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    p: 1,
                    pl: 2,
                    position: "relative",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#333",
                    }}
                  >
                    Specifications
                  </Typography>
                  <InfoOutlined
                    onClick={handleInfoPopupOpen}
                    sx={{
                      fontSize: "20px",
                      color: "#666",
                      cursor: "pointer",
                    }}
                  />

                  {/* Info Popup */}
                  {isInfoPopupOpen && (
                    <Box
                      ref={infoPopupRef}
                      sx={{
                        position: "absolute",
                        top: "calc(100%)",
                        left: 130,
                        width: "339px",
                        height: "130px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        zIndex: 1000,
                        p: 2,
                        "@media (max-width: 856px)": {
                          left: 10,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          mb: 1,
                        }}
                      >
                        Optional Details
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                          mb: 1,
                        }}
                      >
                        Provided by the Seller
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          lineHeight: 1.4,
                        }}
                      >
                        These details are shared by the seller and may help you better
                        understand the product.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Divider */}
                <Box
                  sx={{
                    width: "100%",
                    height: "1px",
                    backgroundColor: "#E0E0E0",
                    mb: 3,
                  }}
                />

                {/* Specifications Content */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    p: 2,
                    pt: 0,
                  }}
                >
                  {specifications.map(spec => (
                    <Box key={spec.id} sx={{ display: "flex" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          width: "304px",
                          "@media (max-width: 1360px)": { width: "204px" },
                        }}
                      >
                        {spec.label}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#666" }}>
                        {spec.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Fullscreen Image Popup */}
        <FullscreenImagePopup
          open={isFullscreenOpen}
          onClose={handleFullscreenClose}
          images={productImages}
          currentIndex={currentImageIndex}
          onPrevious={handleFullscreenPrevious}
          onNext={handleFullscreenNext}
        />

        {/* Unfollow Confirmation Popup */}
          <AlertPopup
            open={unfollowPopup.open}
            title="Unfollow This Person"
            message={
              <>
                Are you sure you want to unfollow user{" "}
                <strong>{unfollowPopup.followingName}</strong>?
              </>
            }
            confirmText="YES, UNFOLLOW"
            cancelText="CANCEL"
            onConfirm={handleConfirmUnfollow}
            onCancel={handleCancelUnfollow}
            onClose={handleCancelUnfollow}
            showCheckCircle={true}
          />

        {/* Alert Popup for add collection actions */} 
            <AlertPopup
                open={successPopup.open}
                title={`Item "${successPopup.title}" is added to your collection.`}
                cancelText=""
                confirmText="OK"
                onConfirm={handleConfirmAdd}
                onClose={() => setSuccessPopup({ open: false, title: "" })}
              />

            {/* Alert Popup for remove collection actions */} 
            <AlertPopup
                open={confirmPopup.open}
                title={`Are you sure you want to remove "${confirmPopup.title}" from your collection?`}
                confirmText="Yes, Remove"
                cancelText="Cancel"
                onConfirm={handleConfirmRemove}
                onCancel={() => setConfirmPopup({ open: false, title: "" })}
                onClose={() => setConfirmPopup({ open: false, title: "" })}
              />
            

        <CommentDialog
          open={commentDialog.open}
          product={productData}
          comments={productData && productData.comments 
                    ? productData.comments 
                    : []
                    }
          onAddComment={text => handleAddComment(text)}
          onAddReply={(reply, commentUuid) =>
            handleAddReply(reply, commentUuid)
          }
          onFollowChange={() => handleFollowToggle}
          onCommentLikeToggle={(newLikeState, commentUuid) =>
            handleCommentLikeUpdate(commentUuid, newLikeState)
          }
          onCommentReplyLikeToggle={(
            newLikeState,
            commentUuid,
            commentReplyUuid
          ) =>
            handleCommentReplyLikeUpdate(
              commentUuid,
              commentReplyUuid,
              newLikeState,
            )
          }
          onDeleteComment={commentUuid => handleDeleteComment(commentUuid)}
          onDeleteReply={(commentUuid, replyUuid) =>
            handleDeleteReply(commentUuid, replyUuid)
          }
          onEditComment={(commentUuid, newText) => handleEditComment(commentUuid, newText)}
          onEditReply={(commentUuid, replyUuid, newText) =>
            handleEditReply(commentUuid, replyUuid, newText)
          }
          onClose={handleCloseCommentDlg}
        />

        {/* Report Popup */}
        <ReportDialog
          open={reportDialog.open}
          onClose={handleReportDialogClose}
          contentType="toy"
          objectId={productData.toyId}
          objectUuid={toyUuid}
        />

        {/* Share Popup */}
        <SharePopup
          open={shareDialog.open}
          onClose={handleShareDialogClose}
          shareUrl="https://dasbazi.com/toy/yR14Cb42mbVnDf"
          contentTitle={productData && productData.title}
          onEmailShareSuccess={handleToyShareSuccess}
        />
        
        <AlertPopup
          open={warningPopup.open}
          title={warningPopup.title}
          message=""
          confirmText="OK"
          cancelText= ""
          onConfirm={() => setWarningPopup(false)}
          onClose={() => setWarningPopup(false)}
        />

        <FloatingMenu left={floatMenuLeftMargin} />
      </Box>
    </Box>
  ) : (
      (isInvalidURL &&
        <Box sx={{ textAlign: "center", mb: 4, mt: 10 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: "30px",
              "@media (max-width: 856px)": {
                fontSize: "24px",
              },
              fontWeight: "700",
              color: "#333",
              mb: 1,
            }}
          >
            This URL is not valid.
          </Typography>
          <Button
          variant="contained"
          onClick={() => navigate("/dashboard")}
          sx={{
            height: "42px",
            px: 3,
            mt: 5,
            fontWeight: 500,
            backgroundColor: "#EF5658",
            "@media (max-width: 856px)": {
              fontSize: "13px",
              height: "30px",
              px: 1,
            },
          }}
        >
          OK
        </Button>
        </Box>
      )
  )}
  </>
  );
};

export default ProductDetail;
